import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Turbolinks from 'turbolinks'
import {
  Box,
  Title,
  ThemeProvider,
  Form,
  Input,
  Textarea,
  Checkbox,
  CMSContent,
  ExpandableContent,
  RatingInput,
  Media,
  Text,
  Layout,
  Level,
  Placeholder,
  Image,
  Link,
  ErrorBoundary,
  MediaQuery,
  Columns,
} from '@deseretbook/react-ui'
import { useUserReview } from './useUserReview'
import { DeseretBook } from '../../../dbcom/api'
import { Logger } from '../../../dbcom'

const logger = new Logger({ context: 'product/reviews/CreateReviewPage' })

/**
 * This is the Product Preview for the Create a Review component
 * @param {obj} product contains all product info
 * @returns {func} Component
 */
const ProductPreview = ({ product }) => (
  <MediaQuery.Medium>
    {isMediumOrLarger => (
      <Box maxWidth={isMediumOrLarger ? 25 : '100%'}>
        <Media>
          <Media.Figure verticalAlignment="center" size="medium">
            <Link href={`/products/${product.id}`}>
              <Image
                src={product.master.images[0].productUrl}
                alt={product.name}
              />
            </Link>
          </Media.Figure>
          <Media.Content>
            <Title
              title={product.name}
              titleProps={{ href: `/products/${product.id}`, as: 'a' }}
              subtitle={product.subtitle}
              subtitleProps={{ as: 'p' }}
              size={2}
            />
            {product && product.producers && (
              <Box paddingBottom={1}>
                <Text display="block" isItalic>
                  {product.producers
                    .map(({ name }) => name)
                    .filter(Boolean)
                    .join(', ')}
                </Text>
              </Box>
            )}
          </Media.Content>
        </Media>
        {product && product.description.length > 0 && (
          <ExpandableContent collapsedHeight={5}>
            <CMSContent
              dangerouslySetInnerHTML={{ __html: product.description }}
              itsNotDangerousBecause="It is dangerous!"
            />
          </ExpandableContent>
        )}
      </Box>
    )}
  </MediaQuery.Medium>
)

ProductPreview.propTypes = {
  product: PropTypes.shape({
    description: PropTypes.string,
    producers: PropTypes.array,
    subtitle: PropTypes.string,
    name: PropTypes.string,
    master: PropTypes.array,
    id: PropTypes.string,
  }),
}

/**
 * This is the Create Review Component
 * @param {number} productId - the Products Id
 * @param {string} starRating - Rating from product page
 * Only shows if the user selected a star rating before click write a full review
 * @returns {func} Component
 */
const CreateReviewPage = ({ productId, starRating = '' }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setLoading] = useState(true)
  const [product, setProduct] = useState()
  const [showName, setShowName] = useState(true)
  const [formValue, setFormValue] = useState({
    rating: starRating,
    title: '',
    review: '',
    name: (window.__USER__ || {}).name || '',
  })

  const currentUser = window.__USER__

  const [userReview, createOrUpdateReview, reviewError] = useUserReview(
    currentUser && currentUser.id,
    productId,
  )

  const productPage = `/products/${productId}`

  /**
   * Fetches the Product and sets the product Variable
   */
  const getProduct = async () => {
    try {
      const productResponse = await DeseretBook.product({ id: productId }).get()

      if (productResponse) {
        setProduct(productResponse)
      }
    } catch (error) {
      // Fails silently, It's not necessary to see ProductReviewPreview
      logger.error(`Error obtaining product info: ${error}`)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Sets the formValue state on input change
   * @param {obj} name - Name of the input
   * @param {string} value - The value returned by input
   */
  const handleChange = ({ name, value }) => {
    setFormValue(currentFormValue => ({ ...currentFormValue, [name]: value }))
  }

  /**
   * Submits the review. Redirects to product page on success.
   * @param {obj} event - the event object
   */
  const handleSubmit = async event => {
    if (!currentUser) {
      Turbolinks.visit(productPage)
    }

    event.preventDefault()
    setIsSubmitting(true)

    try {
      const userId = Number(currentUser.id)

      const review = {
        ...formValue,
        userId,
        name: showName ? formValue.name : null,
      }

      createOrUpdateReview(review)

      if (reviewError) {
        throw new Error(reviewError)
      }

      // Prevents resubmitting another form after initial submit
      Turbolinks.clearCache()
      Turbolinks.visit(productPage)
    } catch (error) {
      logger.error(`Error while submitting: ${error}`)
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    getProduct()
  }, [])

  useEffect(() => {
    if (userReview) {
      const { rating, title, review, name } = userReview

      // Sets the default form values if the user has already written a review
      setFormValue({
        rating: String(rating),
        title,
        review,
        name,
      })
    }
  }, [userReview])

  return (
    <ThemeProvider>
      <Layout>
        {!!(product || isLoading) && (
          <ErrorBoundary failSilently>
            <Layout.Aside>
              <Layout.Section>
                {isLoading ? (
                  <Placeholder>
                    <Box minWidth={14}>
                      <Media>
                        <Media.Figure verticalAlignment="center" size="large">
                          <Placeholder.Block width={14} height={8} />
                        </Media.Figure>
                        <Media.Content>
                          <Placeholder.Block
                            marginBottom={1.5}
                            width={8}
                            height={2}
                          />
                          <Placeholder.Text lines={2} />
                        </Media.Content>
                      </Media>
                      <Placeholder.Text lines={5} />
                    </Box>
                  </Placeholder>
                ) : (
                  <ProductPreview product={product} />
                )}
              </Layout.Section>
            </Layout.Aside>
          </ErrorBoundary>
        )}
        <Layout.Content>
          <Layout.Section>
            <Title
              title="Leave us a review"
              size={1}
              titleProps={{ as: 'h1' }}
            />
            <Form isSubmitting={isSubmitting} onSubmit={handleSubmit}>
              <RatingInput
                name="rating"
                aria-label="How would you rate this product on a scale of 1 to 5?"
                onChange={({ target }) => handleChange(target)}
                selectedValue={formValue.rating}
                required
                removeHint
                size="medium"
              />
              <Columns>
                <Columns.Column size={{ small: 6, large: 4 }}>
                  <Box maxWidth={25}>
                    <Input
                      name="name"
                      type="text"
                      label="Display Name"
                      required={showName}
                      disabled={!showName}
                      hint={
                        showName
                          ? 'The name to be displayed with your review'
                          : 'Your review will be completely anonymous'
                      }
                      value={showName ? formValue.name : ''}
                      maxLength={30}
                      onChange={({ target }) => handleChange(target)}
                    />
                  </Box>
                </Columns.Column>
                <Columns.Column size={{ small: 6, large: 8 }}>
                  <Checkbox
                    onChange={() => setShowName(!showName)}
                    name="anonymous"
                    value={showName}
                    checked={showName}
                    removeHint
                  >
                    Show my name with review?
                  </Checkbox>
                </Columns.Column>
              </Columns>
              <Input
                name="title"
                type="text"
                label="Add a Title"
                hint="What's the first thing others should know?"
                value={formValue.title}
                required
                maxLength={50}
                onChange={({ target }) => handleChange(target)}
              />
              <Textarea
                name="review"
                label="My Review"
                required
                hint="What did you think about the product? Describe your experience."
                value={formValue.review}
                onChange={({ target }) => handleChange(target)}
              />
              <Level>
                <Level.Left isShrinkable>
                  <Level.Item>
                    <Text color="secondary" shade="light" size="tiny">
                      Note: It may take us up to two days to approve your review
                      before others can see it.
                    </Text>
                  </Level.Item>
                </Level.Left>
                <Level.Right>
                  <Level.Item>
                    <Form.SubmitButton color="action">Submit</Form.SubmitButton>
                  </Level.Item>
                </Level.Right>
              </Level>
            </Form>
          </Layout.Section>
        </Layout.Content>
      </Layout>
    </ThemeProvider>
  )
}

export default CreateReviewPage

CreateReviewPage.propTypes = {
  productId: PropTypes.number.isRequired,
  starRating: PropTypes.string,
}
